import {Component, ElementRef, HostListener, effect, input, output, viewChild} from '@angular/core';

/**
 * Confirms starting a TSE data export (#776) — producing the security device's own signed log messages — in a
 * modal, mirroring `technical-security-system/tss-reinitialize-confirm-dialog.component.ts`'s shape:
 * backdrop/Escape cancel, focus starts on the safe „Abbrechen" button, `busy` disables the confirm button
 * while the call is in flight.
 *
 * ### Why a confirmation at all
 *
 * Producing an export occupies the security device, and while it runs the device may be unable to sign
 * transactions — a large export can hold it for minutes. That is not a risk an operator should meet
 * afterwards, in a booking that was signed late.
 *
 * ### Why it warns and does not block
 *
 * The obvious alternative is to refuse while a cash point is open, the way a TSE re-initialisation does. That
 * was considered and deliberately rejected. A re-initialisation retires a device permanently and a closing
 * signed across the change would name two devices, so there the refusal protects a document. Here the risk is
 * a delay, not a loss: an order captured while the device is busy is signed late or documents the failure, and
 * the booking pipeline already handles both. Against that, a hard block would make the export impossible for a
 * venue that trades around the clock, and unusable for an adviser working to a deadline — the two people this
 * whole feature exists for. So the risk is stated plainly, once, and the operator decides.
 *
 * ### The wording
 *
 * Formal Sie throughout. One instruction per sentence, what happens before what to do, and the supplier of the
 * device is not named — the constraint the whole of Feature #241 exists to satisfy.
 */
@Component({
  selector: 'legal-tse-export-confirm-dialog',
  standalone: true,
  template: `
    <div class="fixed inset-0 z-[1100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
         (click)="cancel()">
      <div class="w-full max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl"
           role="alertdialog" aria-modal="true" aria-labelledby="tse-export-heading"
           aria-describedby="tse-export-body" (click)="$event.stopPropagation()">
        <div class="space-y-4 p-4 sm:p-6">

          <h2 id="tse-export-heading" class="text-xl font-bold tracking-tight text-gray-900">
            Export der Sicherheitseinrichtung starten?
          </h2>

          <div id="tse-export-body" class="space-y-3 text-sm text-gray-700">
            <p>Bitte beachten Sie:</p>
            <ul class="list-disc space-y-1.5 pl-5">
              <li>Während der Export läuft, ist die Sicherheitseinrichtung mit ihm beschäftigt.</li>
              <li>Sie kann in dieser Zeit möglicherweise keine Buchungen signieren.</li>
              <li>Je größer der Zeitraum ist, desto länger dauert der Export.</li>
              <li>Starten Sie ihn deshalb am besten außerhalb der Geschäftszeiten.</li>
            </ul>
            <p>
              Ihre Buchungen gehen dabei nicht verloren. Eine Buchung, die während des Exports erfasst wird,
              wird nachträglich signiert oder mit dem Fehler dokumentiert.
            </p>
          </div>

          <div class="flex justify-end gap-3 border-t border-gray-100 pt-4">
            <button type="button" #cancelButton (click)="cancel()"
                    class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4
                           py-2.5 text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                           hover:border-gray-300 hover:bg-gray-50 active:scale-95">
              Abbrechen
            </button>
            <button type="button" (click)="confirm()" [disabled]="busy()"
                    class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-700 px-5 py-2.5
                           text-sm font-semibold text-white shadow-lg transition-all duration-200
                           hover:bg-theme-primary-800 active:scale-95 disabled:cursor-not-allowed
                           disabled:opacity-30 disabled:grayscale">
              Ja, Export starten
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class TseExportConfirmDialogComponent {
  /** Disables the confirm button while the create call is in flight. */
  readonly busy = input(false);

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  constructor() {
    // Focus starts on the SAFE action. The dialog is opened by a button the operator just pressed, so a
    // stray Enter would otherwise confirm something they have not read yet.
    effect(() => {
      const button = this.cancelButton()?.nativeElement;
      if (button) button.focus();
    });
  }

  protected confirm(): void {
    if (this.busy()) return;
    this.confirmed.emit();
  }

  protected cancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.cancel();
  }
}
